import React from 'react';
import {
  PencilSquareIcon,
  TrashIcon,
  StarIcon,
  ArchiveBoxIcon,
  EllipsisVerticalIcon,
  TagIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { RichNote } from '../../types/richNotes';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';

interface RichNoteCardProps {
  note: RichNote;
  onEdit: (note: RichNote) => void;
  onDelete: (noteId: string) => void;
  onToggleFavorite: (noteId: string) => void;
  onTogglePin: (noteId: string) => void;
  onArchive: (noteId: string) => void;
  onClick: (note: RichNote) => void;
}

const RichNoteCard: React.FC<RichNoteCardProps> = ({
  note,
  onEdit,
  onDelete,
  onToggleFavorite,
  onTogglePin,
  onArchive,
  onClick,
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
    orange: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
    red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    pink: 'bg-pink-50 dark:bg-pink-900/20 border-pink-200 dark:border-pink-800',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    gray: 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800',
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Most';
    if (diffMins < 60) return `${diffMins} perce`;
    if (diffHours < 24) return `${diffHours} órája`;
    if (diffDays < 7) return `${diffDays} napja`;
    
    return date.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getPreviewText = (content: string, maxLength: number = 150) => {
    const text = stripHtml(content);
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <div
      className={`relative group rounded-xl border-2 ${colorClasses[note.color]} p-4 hover:shadow-lg transition-all duration-200 cursor-pointer`}
      onClick={() => onClick(note)}
    >
      {/* Pin indicator */}
      {note.isPinned && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shadow-md z-10">
          <span className="text-white text-xs">📌</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0 pr-2">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">
            {note.title}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
              <ClockIcon className="w-3 h-3" />
              {formatDate(note.updatedAt)}
            </span>
            {note.category && (
              <span className="text-xs px-2 py-0.5 bg-white/50 dark:bg-black/20 rounded-full text-gray-600 dark:text-gray-400">
                {note.category}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(note.id);
            }}
            className="p-1.5 hover:bg-white/50 dark:hover:bg-black/20 rounded-lg transition-colors"
            title={note.isFavorite ? 'Kedvencekből eltávolítás' : 'Kedvencekhez adás'}
          >
            {note.isFavorite ? (
              <StarIconSolid className="w-4 h-4 text-yellow-500" />
            ) : (
              <StarIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            )}
          </button>

          <Menu as="div" className="relative">
            <Menu.Button
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              className="p-1.5 hover:bg-white/50 dark:hover:bg-black/20 rounded-lg transition-colors"
            >
              <EllipsisVerticalIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-20">
                <div className="p-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(note);
                        }}
                        className={`${
                          active ? 'bg-gray-100 dark:bg-gray-700' : ''
                        } group flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-700 dark:text-gray-300`}
                      >
                        <PencilSquareIcon className="mr-2 h-4 w-4" />
                        Szerkesztés
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onTogglePin(note.id);
                        }}
                        className={`${
                          active ? 'bg-gray-100 dark:bg-gray-700' : ''
                        } group flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-700 dark:text-gray-300`}
                      >
                        📌
                        <span className="ml-2">{note.isPinned ? 'Rögzítés feloldása' : 'Rögzítés'}</span>
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onArchive(note.id);
                        }}
                        className={`${
                          active ? 'bg-gray-100 dark:bg-gray-700' : ''
                        } group flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-700 dark:text-gray-300`}
                      >
                        <ArchiveBoxIcon className="mr-2 h-4 w-4" />
                        Archiválás
                      </button>
                    )}
                  </Menu.Item>
                  <div className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(note.id);
                        }}
                        className={`${
                          active ? 'bg-red-50 dark:bg-red-900/20' : ''
                        } group flex w-full items-center rounded-md px-3 py-2 text-sm text-red-600 dark:text-red-400`}
                      >
                        <TrashIcon className="mr-2 h-4 w-4" />
                        Törlés
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

      {/* Content preview */}
      <div className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">
        {getPreviewText(note.content)}
      </div>

      {/* Tags */}
      {note.tags && note.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {note.tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-white/70 dark:bg-black/30 rounded-full text-gray-600 dark:text-gray-400"
            >
              <TagIcon className="w-3 h-3" />
              {tag}
            </span>
          ))}
          {note.tags.length > 3 && (
            <span className="text-xs px-2 py-0.5 bg-white/70 dark:bg-black/30 rounded-full text-gray-600 dark:text-gray-400">
              +{note.tags.length - 3}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default RichNoteCard;
